import { AlertCircle, FileType, Loader2, Upload } from 'lucide-react';
import type React from 'react';
import { useCallback, useState } from 'react';
import type { STLAnalysis } from '../types';
import { parseSTL } from '../utils/stlParser';

interface STLDropzoneProps {
  onFileAnalyzed: (analysis: STLAnalysis, file: File) => void;
}

const STLDropzone: React.FC<STLDropzoneProps> = ({ onFileAnalyzed }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setIsProcessing(true);
      setProgress(0);

      // Validate file type
      if (!file.name.toLowerCase().endsWith('.stl')) {
        setError('Please upload an STL file (.stl)');
        setIsProcessing(false);
        return;
      }

      // Validate file size (50MB max)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        setError('File is too large! Maximum size is 50MB');
        setIsProcessing(false);
        return;
      }

      try {
        // Simulate progress for better UX
        setProgress(20);

        // Parse the STL file
        const analysis = await parseSTL(file);
        setProgress(80);

        if (!analysis.isValid) {
          setError(analysis.error || 'Could not parse STL file');
          setIsProcessing(false);
          return;
        }

        setProgress(100);

        // Small delay for smooth transition
        setTimeout(() => {
          onFileAnalyzed(analysis, file);
          setIsProcessing(false);
        }, 300);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process file');
        setIsProcessing(false);
      }
    },
    [onFileAnalyzed]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative rounded-2xl border-2 border-dashed p-12 text-center transition-all duration-200
          ${isDragging ? 'border-blue-400 bg-blue-500/10 scale-[1.02]' : 'hover:border-blue-500'}
          ${isProcessing ? 'pointer-events-none opacity-70' : 'cursor-pointer'}
        `}
        style={{
          borderColor: isDragging ? undefined : 'var(--border-color)',
          backgroundColor: isDragging ? undefined : 'var(--surface-inactive)',
        }}
      >
        <input
          type="file"
          accept=".stl"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />

        {isProcessing ? (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 mx-auto text-accent-blue animate-spin" />
            <p className="font-bold text-xl" style={{ color: 'var(--text-primary)' }}>
              Analyzing your model...
            </p>
            <div
              className="max-w-md mx-auto rounded-full h-3 overflow-hidden"
              style={{ backgroundColor: 'var(--surface-inactive)' }}
            >
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {progress}% complete
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
              <Upload className="w-10 h-10 text-on-gradient" />
            </div>
            <div>
              <p className="font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>
                Drop your 3D model file here
              </p>
              <p style={{ color: 'var(--text-muted)' }}>or click to browse your downloads</p>
            </div>
            <div
              className="flex items-center justify-center gap-2 text-sm"
              style={{ color: 'var(--text-muted)' }}
            >
              <FileType className="w-4 h-4" />
              <span>
                Looking for a <strong>.stl</strong> file (up to 50MB)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/50 text-error rounded-xl flex items-start gap-3 animate-fade-in">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold mb-1">Oops!</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Help Text */}
      {!isProcessing && !error && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl space-y-2">
          <p className="text-info text-sm">
            <strong>What's an STL file?</strong> It's the 3D model file that your favorite models
            use!
          </p>
          <p className="text-info text-sm">
            <strong>How to get it:</strong>
          </p>
          <ol className="text-info text-sm list-decimal list-inside space-y-1 ml-2">
            <li>
              Find a cool model on <strong>Printables</strong> or <strong>MakerWorld</strong>
            </li>
            <li>
              Click the <strong>"Download"</strong> button
            </li>
            <li>
              Look for the file ending in <strong>.stl</strong> (usually downloads automatically)
            </li>
            <li>Drag that file here to see your price!</li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default STLDropzone;
